/**
 * Hero Scramble Animation
 * Scrambles the homepage hero H1 ("OFF PISTE"), the hero description paragraph,
 * and the hero call-to-action buttons on load — a "decrypting" load-in effect
 * using GSAP's ScrambleTextPlugin.
 */

(function () {
  'use strict';

  var SCRAMBLE_CHARS = '01XO!@#$%^&*'; // tech/digital character set

  // Wait for GSAP to be loaded
  function waitForGSAP(callback, attempts) {
    attempts = attempts || 0;
    if (typeof gsap !== 'undefined' && typeof gsap.registerPlugin === 'function') {
      callback();
    } else if (attempts < 50) {
      // Give up after ~5s so a blocked/absent CDN can't poll forever.
      setTimeout(function () { waitForGSAP(callback, attempts + 1); }, 100);
    } else {
      // GSAP never loaded: make sure the hero text is visible with its authored text.
      revealAllTargets();
    }
  }

  var HERO_H1_SEL = '.hero__content-wrapper > [data-layer] > .text-block > *';
  var HERO_DESC_SEL = '.hero__content-wrapper .group-block .text-block > *';
  var HERO_BTN_SEL = '.hero__content-wrapper a.button, .hero__content-wrapper a.button-secondary';

  // Make every scramble target visible with its authored (final) text.
  // Nothing in the theme CSS hides them before the scramble, so this is a
  // safety net for skipped / failed / throttled animations.
  function revealAllTargets() {
    document.querySelectorAll(HERO_H1_SEL + ', ' + HERO_DESC_SEL + ', ' + HERO_BTN_SEL).forEach(function (el) {
      el.style.opacity = '1';
    });
  }

  function scramble(el, opts) {
    opts = opts || {};
    // Final text is captured BEFORE scrambling so it can be restored by the failsafe.
    var raw = el.textContent;
    var originalText = opts.trim === false ? raw : raw.trim();
    if (!originalText) return;
    el.style.opacity = '1';
    // Keep the real text available to assistive tech while characters scramble.
    var hadLabel = el.hasAttribute('aria-label');
    if (!hadLabel) el.setAttribute('aria-label', originalText);

    // Lock the element's size to its final (pre-scramble) dimensions so the
    // scrambled characters — which are wider/narrower than the real text in a
    // proportional font — can't reflow it (extra/fewer lines) or make a
    // content-sized element (e.g. a button) jitter horizontally. Released on
    // completion so the element stays responsive to viewport changes after.
    var lockH = opts.lockHeight !== false; // default on
    var lockW = opts.lockWidth === true; // opt-in (buttons)
    var prev = {
      height: el.style.height,
      width: el.style.width,
      overflow: el.style.overflow,
      whiteSpace: el.style.whiteSpace
    };
    var h = lockH ? el.offsetHeight : 0;
    var w = lockW ? el.offsetWidth : 0;
    if (h) {
      el.style.height = h + 'px';
      el.style.overflow = 'hidden';
    }
    if (w) {
      el.style.width = w + 'px';
      el.style.overflow = 'hidden';
      el.style.whiteSpace = 'nowrap';
    }

    var released = false;
    function release() {
      if (released) return;
      released = true;
      if (h) {
        el.style.height = prev.height;
      }
      if (w) {
        el.style.width = prev.width;
        el.style.whiteSpace = prev.whiteSpace;
      }
      if (h || w) {
        el.style.overflow = prev.overflow;
      }
      if (!hadLabel) el.removeAttribute('aria-label');
    }

    var tween = gsap.to(el, {
      duration: opts.duration,
      scrambleText: {
        text: originalText,
        chars: SCRAMBLE_CHARS,
        revealDelay: opts.revealDelay,
        speed: opts.speed,
        tweenLength: false,
        rightToLeft: false
      },
      delay: opts.delay,
      ease: 'power2.out',
      onComplete: function () {
        release();
        if (typeof opts.onComplete === 'function') {
          opts.onComplete();
        }
      }
    });

    // Failsafe: if the tween hasn't finished by delay + duration + 600ms (throttled
    // background tab, thumbnail capture, stalled rAF), snap to the final state.
    setTimeout(function () {
      if (tween && tween.progress() < 1) {
        tween.kill();
        el.textContent = originalText;
        el.style.opacity = '1';
        release();
      }
    }, ((opts.delay || 0) + (opts.duration || 1)) * 1000 + 600);
  }

  function initHeroScrambleAnimation() {
    // Skip entirely for reduced-motion visitors and for tabs that load hidden
    // (animation timers are throttled there): show the final text immediately.
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || document.hidden) {
      revealAllTargets();
      return;
    }
    if (typeof ScrambleTextPlugin === 'undefined') {
      console.error('Hero Scramble: ScrambleTextPlugin not found!');
      revealAllTargets();
      return;
    }
    gsap.registerPlugin(ScrambleTextPlugin);

    // 1) The display headline (e.g. "OFF PISTE") — the hero's own top-level text
    //    block. Selected by position rather than by tag: the headline is an <h2>
    //    and the page's <h1> is the description's lead sentence below, so an
    //    h1-based selector would scramble the wrong element.
    var heroH1s = document.querySelectorAll(HERO_H1_SEL);
    heroH1s.forEach(function (h1, index) {
      scramble(h1, { duration: 1, revealDelay: 0.2, speed: 0.8, delay: index * 0.15 });
    });

    // Description (the page's H1 + Kickstarter status line) and CTA buttons are
    // deliberately NOT scrambled (Oct 31 pre-launch): screen readers would read
    // the garbled text, and the explanatory headline must be readable instantly.
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        setTimeout(initHeroScrambleAnimation, 200);
      });
    } else {
      setTimeout(initHeroScrambleAnimation, 200);
    }

    // Re-run when a hero section is reloaded in the theme editor
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (event) {
        if (event.target.querySelector('.hero__content-wrapper')) {
          setTimeout(initHeroScrambleAnimation, 100);
        }
      });
    }
  }

  waitForGSAP(init);
  // If GSAP is still missing at ~1.5s the scramble can't have started: reveal now
  // (the 5s give-up path above remains as a second net).
  setTimeout(function () {
    if (typeof gsap === 'undefined') revealAllTargets();
  }, 1500);
})();
